import { useState, useEffect } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  selectBusinessInventoryList,
  selectCurrentBusiness,
} from 'redux/selectors';
import {
  createInventoryToProduct,
  getBusinessInventoryList,
  updateInventoryItem,
} from 'redux/actions/inventory/inventory';
import { SET_FORM_PRODUCT } from 'redux/actions/types';
import { useInventoryConfig } from 'components/MenuNew/FormView/Inventory/InventoryConfigContext';
import useQuery from 'hooks/useQuery';
import { getProductById } from 'redux/actions/Menu/products';

const formSchema = yup.object().shape({
  uuid: yup.string().required('Inventory Item is required'),
  qty: yup
    .number()
    .required('Qty is required')
    .typeError('Qty must be a number'),
});

const defaultValues = {
  uuid: '',
  qty: 1,
};

const useCreateInventory = (
  open,
  productId,
  onClose,
  replaceInventory,
  productInventory,
  editingItem,
  productFormMethods
) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const cid = query.get('cid');
  const inventoryConfig = useInventoryConfig();
  const [loading, setLoading] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);

  const currentBusiness = useSelector(selectCurrentBusiness);
  const businessInventoryList = useSelector(selectBusinessInventoryList);

  const inventoryCreateMethod = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: defaultValues,
    mode: 'all',
  });

  useEffect(() => {
    if (!open) {
      inventoryCreateMethod.reset();
      inventoryCreateMethod.clearErrors();
      setSelectedInventoryItem(null);
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (editingItem) {
      inventoryCreateMethod.reset({
        uuid: editingItem.uuid,
        qty: editingItem.qty,
      });
      setSelectedInventoryItem({
        value: editingItem.uuid,
        label: editingItem.name,
        data: editingItem,
      });
    } else {
      inventoryCreateMethod.reset({
        uuid: '',
        qty: 1,
      });
      inventoryCreateMethod.clearErrors();
      setSelectedInventoryItem(null);
    }
  }, [editingItem]);

  const getProduct = (productId) => {
    // Allow a host to override how the owning entity is refetched (e.g. POS
    // menu items fetch from a different endpoint). Falls back to the regular
    // menu product fetch when no override is provided.
    if (inventoryConfig?.refetchProduct) {
      return Promise.resolve(inventoryConfig.refetchProduct(productId)).then(
        (res) => {
          if (res) {
            productFormMethods.reset(res);
          }
          return res;
        }
      );
    }
    return dispatch(getProductById(productId, cid)).then((res) => {
      productFormMethods.reset(res);
      dispatch({ type: SET_FORM_PRODUCT, payload: res });
    });
  };

  const handleReplaceInventory = async () => {
    setLoading(true);
    const requestData = {
      uuid: inventoryCreateMethod.getValues('uuid'),
      qty: Number(inventoryCreateMethod.getValues('qty')),
      isMain: inventoryCreateMethod.getValues('isMain') ?? false,
    };

    try {
      // POS menu items manage inventory on the POS item itself, not via the
      // MMS `/products/:id/inventory` endpoints, so use the host override.
      if (inventoryConfig?.replaceInventory) {
        await inventoryConfig.replaceInventory(requestData, editingItem.uuid);
      } else {
        await dispatch(
          updateInventoryItem(requestData, productId, editingItem.uuid)
        );
      }
      getProduct(productId);
      dispatch(getBusinessInventoryList(currentBusiness?.uuid));

      onClose();
    } catch (error) {
      console.error(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAddInventory = async () => {
    setLoading(true);
    const requestData = {
      uuid: inventoryCreateMethod.getValues('uuid'),
      qty: Number(inventoryCreateMethod.getValues('qty')),
      isMain: false,
    };
    try {
      // POS menu items manage inventory on the POS item itself, not via the
      // MMS `/products/:id/inventory` endpoints, so use the host override.
      if (inventoryConfig?.addInventory) {
        await inventoryConfig.addInventory(requestData);
      } else {
        await createInventoryToProduct(requestData, productId);
      }
      getProduct(productId);
      onClose();
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeInventory = (value) => {
    inventoryCreateMethod.setValue('uuid', value?.value, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setSelectedInventoryItem(value);
  };

  return {
    inventoryCreateMethod,
    handleAddInventory,
    loading,
    filteredBusinessInventoryList: businessInventoryList,
    handleReplaceInventory,
    selectedInventoryItem,
    handleChangeInventory,
  };
};

export default useCreateInventory;
